import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CompanyRequest, CompanyResponse } from "@/types/api";

const editCompanySchema = z.object({
  name: z.string().min(2, "Nome deve ter pelo menos 2 caracteres"),
  cnpj: z.string().min(14, "CNPJ deve ter 14 dígitos").max(18, "CNPJ inválido"),
  telephone: z.string().optional(),
});

type EditCompanyFormData = z.infer<typeof editCompanySchema>;

interface EditCompanyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: CompanyResponse | null;
  onSave?: (data: CompanyRequest) => void;
}

const EditCompanyModal = ({
  open,
  onOpenChange,
  company,
  onSave,
}: EditCompanyModalProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<EditCompanyFormData>({
    resolver: zodResolver(editCompanySchema),
    defaultValues: {
      name: "",
      cnpj: "",
      telephone: "",
    },
  });

  useEffect(() => {
    if (company && open) {
      // Formatar CNPJ para exibição
      const formatCNPJ = (cnpj: string) => {
        const numbers = cnpj.replace(/\D/g, "");
        return numbers
          .replace(/(\d{2})(\d)/, "$1.$2")
          .replace(/(\d{3})(\d)/, "$1.$2")
          .replace(/(\d{3})(\d)/, "$1/$2")
          .replace(/(\d{4})(\d)/, "$1-$2")
          .substring(0, 18);
      };

      form.reset({
        name: company.name,
        cnpj: company.cnpj ? formatCNPJ(company.cnpj) : "",
        telephone: company.telephone || "",
      });
    }
  }, [company, open, form]);

  const formatCNPJ = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    return numbers
      .replace(/(\d{2})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2")
      .substring(0, 18);
  };

  const onSubmit = async (data: EditCompanyFormData) => {
    setIsLoading(true);
    try {
      if (onSave) {
        // Remove formatação do CNPJ para enviar apenas números
        const cleanCnpj = data.cnpj.replace(/\D/g, "");
        onSave({
          name: data.name,
          cnpj: cleanCnpj,
          telephone: data.telephone,
        });
      }

      onOpenChange(false);
    } catch (error) {
      console.error("Erro ao atualizar empresa:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Editar Empresa</DialogTitle>
          <DialogDescription>Edite as informações da empresa</DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="name">Nome da Empresa</Label>
            <Input
              id="name"
              {...form.register("name")}
              className="mt-1"
              placeholder="Digite o nome da empresa"
            />
            {form.formState.errors.name && (
              <p className="text-sm text-destructive mt-1">
                {form.formState.errors.name.message}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="cnpj">CNPJ</Label>
            <Input
              id="cnpj"
              {...form.register("cnpj")}
              onChange={(e) => {
                const formatted = formatCNPJ(e.target.value);
                form.setValue("cnpj", formatted);
              }}
              className="mt-1"
              placeholder="00.000.000/0000-00"
              maxLength={18}
            />
            {form.formState.errors.cnpj && (
              <p className="text-sm text-destructive mt-1">
                {form.formState.errors.cnpj.message}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="telephone">Telefone</Label>
            <Input
              id="telephone"
              {...form.register("telephone")}
              className="mt-1"
              placeholder="(00) 00000-0000"
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Salvando..." : "Salvar Alterações"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditCompanyModal;
